import * as THREE from "three";
import { CameraControls } from '@react-three/drei';

import { useCameraControlsStore } from "../store/cameraControlsStore";
import { CrosshairController } from "./CrosshairController";

export default class TouchInput {

    #isTouching?: boolean;
    #firstTouch?: THREE.Vector2;
    #lastTouch?: THREE.Vector2;
    #touchDelay: number = 200;
    #touchTolerance: number = 10; // 10 pixels

    #timeOut?: NodeJS.Timeout;
    #scene?: THREE.Scene;
    #crossHair: CrosshairController = new CrosshairController(); 

    #touchPointOffset = new THREE.Vector2(0, -80); // Offset touch point slightly above finger
    #enabled: boolean = true;
    #savedDollySpeed?: number;
    #touchZoomDollySpeed: number = 20;

    set touchZoomDollySpeed(value: number) { this.#touchZoomDollySpeed = value; }

    constructor() {
        this.#crossHair.setPhysicalSizeCm(3);
    
    }

    enable = () => { 
        this.#enabled = true; 
    }
    disable = () => { 
        this.#enabled = false; 
        this.#isTouching = false;
        clearTimeout(this.#timeOut);
    }

    getOffsetPoint(touchEvent?: TouchEvent) : THREE.Vector2 | undefined {
        if (touchEvent && touchEvent.touches.length == 1) {
            return new THREE.Vector2(
                touchEvent.touches[0].clientX + this.#touchPointOffset.x,
                touchEvent.touches[0].clientY + this.#touchPointOffset.y
            );
        }
        return undefined;
    }

    setScene(scene: THREE.Scene) {
        this.#scene = scene;
    }

    onPointerMove: ((event: THREE.Vector2) => void) | undefined;
    onPointerClick: ((event: THREE.Vector2) => void) | undefined;
    onCancel: (() => void) | undefined;

    attachToCanvas(canvas: HTMLCanvasElement) {
        canvas.addEventListener('touchstart', this.handleTouchStart);
        canvas.addEventListener('touchmove', this.handleTouchMove);
        canvas.addEventListener('touchend', this.handleTouchEnd);
    }
    
    detachFromCanvas(canvas: HTMLCanvasElement) {
        canvas.removeEventListener('touchstart', this.handleTouchStart);
        canvas.removeEventListener('touchmove', this.handleTouchMove);
        canvas.removeEventListener('touchend', this.handleTouchEnd);
    }

    setIsTouching(isTouching: boolean) {
        this.#isTouching = isTouching;
        if (useCameraControlsStore.getState().controls)
            useCameraControlsStore.getState().controls!.enabled = !isTouching;
    }

    #setTouchZoomSpeed(active: boolean) {
        const controls = useCameraControlsStore.getState().controls;
        if (!controls) return;
        if (active) {
            this.#savedDollySpeed = controls.dollySpeed;
            controls.dollySpeed = this.#touchZoomDollySpeed;
        } else {
            if (this.#savedDollySpeed !== undefined)
                controls.dollySpeed = this.#savedDollySpeed;
            this.#savedDollySpeed = undefined;
        }
    }

    handleTouchStart = (event: TouchEvent) => {

        if (event.touches.length >= 2) {
            if (this.#isTouching) {
                // Second finger while dragging in precise mode signals cancel
                clearTimeout(this.#timeOut);
                this.setIsTouching(false);
                this.#crossHair.hide();
                this.onCancel?.();
                return;
            }
            this.#setTouchZoomSpeed(true);
            return;
        }

        if (!this.#enabled || !this.onPointerMove)
            return;

        const touchPoint = this.getOffsetPoint(event);
        if (!touchPoint || event.touches.length > 1)
            return;

        event.preventDefault();
        event.stopPropagation();

        this.#firstTouch = touchPoint.clone();
        this.#lastTouch = touchPoint.clone();
        
        this.#timeOut = setTimeout(() => {

            // If touch has moved, reset
            if (this.#firstTouch!.distanceTo(this.#lastTouch!) > this.#touchTolerance) {
                this.#firstTouch = undefined;
                this.#lastTouch = undefined;
                this.setIsTouching(false);
                return;
            }

            this.setIsTouching(true);
            this.handleTouchMove(event);

        }, this.#touchDelay);

    }

    handleTouchMove = (event: TouchEvent) => {

        if (!this.#enabled)
            return;

        event.preventDefault();

        const touchPoint = this.getOffsetPoint(event);
        if (!touchPoint)
            return;

        this.#lastTouch = touchPoint.clone();

        if (this.#isTouching)
        {
            this.#crossHair.setPosition(this.#lastTouch.x, this.#lastTouch.y);
            this.onPointerMove?.(this.#lastTouch);
        }
    }

    handleTouchEnd = (event: TouchEvent) => {

        if (event.touches.length < 2)
            this.#setTouchZoomSpeed(false);

        clearTimeout(this.#timeOut);

        event.preventDefault();
        
        if (this.#isTouching) {
            // Make a "mouse click"
            if (this.onPointerClick && this.#lastTouch)
                this.onPointerClick(this.#lastTouch)
            
            this.setIsTouching(false);
            this.#crossHair.hide();
        }
    }
}